<template>
  <div v-if="isVisible" ref="topDownload" class="top-download">
    <button
      type="button"
      class="top-download__close"
      aria-label="close"
      @click="closeDownload"
    >
      <van-icon name="cross" color="#ffffff" size="14" />
    </button>
    <div ref="lottieHost" class="top-download__lottie" />
    <div class="top-download__text">
      <div class="top-download__title">{{ titleText }}</div>
      <div class="top-download__sub">{{ subText }}</div>
    </div>
    <button type="button" class="top-download__cta" @click="installApp">
      {{ $lang.common_txt64 || 'Download' }}
    </button>
  </div>
</template>
<script>
import lottie from 'lottie-web'
import giftAnimation from '@/assets/lottie/top-download-gift.json'

function isRunningAsInstalledPwa() {
  if (typeof window === 'undefined') return false
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return true
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true
  } catch (e) {
    /* ignore */
  }
  if (window.navigator && window.navigator.standalone === true) return true
  return false
}

export default {
  name: 'TopDownload',
  components: {},
  data() {
    return {
      isShow: true,
      deferredPrompt: null,
      lottieAnim: null,
      inStandaloneMode: isRunningAsInstalledPwa()
    }
  },
  computed: {
    isVisible() {
      return (
        this.isShow &&
        !this.$route.query.app &&
        !this.inStandaloneMode
      )
    },
    titleText() {
      return this.$lang.top_download_title || 'APPLICATION'
    },
    subText() {
      return (
        this.$lang.top_download_sub ||
        'Unlock Fun with Exclusive Features'
      )
    }
  },
  watch: {
    isVisible(visible) {
      this.$nextTick(() => {
        if (visible) this.initLottie()
        else this.destroyLottie()
        this.emitLayoutChange()
      })
    }
  },
  mounted() {
    this.syncStandaloneFromDisplayMode()
    if (typeof window !== 'undefined' && window.matchMedia) {
      this._dmListener = () => this.syncStandaloneFromDisplayMode()
      this._dmMql = window.matchMedia('(display-mode: standalone)')
      if (this._dmMql.addEventListener) {
        this._dmMql.addEventListener('change', this._dmListener)
      } else if (this._dmMql.addListener) {
        this._dmMql.addListener(this._dmListener)
      }
    }
    this.onBeforeInstallPromptBound = this.onBeforeInstallPrompt.bind(this)
    this.onAppInstalledBound = this.onAppInstalled.bind(this)
    window.addEventListener(
      'beforeinstallprompt',
      this.onBeforeInstallPromptBound
    )
    window.addEventListener('appinstalled', this.onAppInstalledBound)

    this.$nextTick(() => {
      this.initLottie()
      this.emitLayoutChange()
      this.checkInstalledRelatedApps()
    })
    window.addEventListener('resize', this.emitLayoutChange)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.emitLayoutChange)
    window.removeEventListener(
      'beforeinstallprompt',
      this.onBeforeInstallPromptBound
    )
    window.removeEventListener('appinstalled', this.onAppInstalledBound)
    if (this._dmMql && this._dmListener) {
      if (this._dmMql.removeEventListener) {
        this._dmMql.removeEventListener('change', this._dmListener)
      } else if (this._dmMql.removeListener) {
        this._dmMql.removeListener(this._dmListener)
      }
    }
    this.destroyLottie()
  },
  methods: {
    syncStandaloneFromDisplayMode() {
      const next = isRunningAsInstalledPwa()
      if (next !== this.inStandaloneMode) {
        this.inStandaloneMode = next
      }
    },
    async checkInstalledRelatedApps() {
      if (!navigator.getInstalledRelatedApps) return
      try {
        const list = await navigator.getInstalledRelatedApps()
        if (list && list.length > 0) {
          this.inStandaloneMode = true
        }
      } catch (e) {
        /* ignore */
      }
    },
    onBeforeInstallPrompt(e) {
      e.preventDefault()
      this.deferredPrompt = e
      this.emitLayoutChange()
    },
    onAppInstalled() {
      this.deferredPrompt = null
      this.inStandaloneMode = true
      this.isShow = false
      this.$nextTick(this.emitLayoutChange)
    },
    initLottie() {
      if (!this.isVisible || !this.$refs.lottieHost || this.lottieAnim) return
      this.lottieAnim = lottie.loadAnimation({
        container: this.$refs.lottieHost,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: giftAnimation
      })
      this.$nextTick(this.emitLayoutChange)
    },
    destroyLottie() {
      if (this.lottieAnim) {
        this.lottieAnim.destroy()
        this.lottieAnim = null
      }
    },
    closeDownload() {
      this.isShow = false
      this.$nextTick(this.emitLayoutChange)
    },
    emitLayoutChange() {
      const height =
        this.isVisible && this.$refs.topDownload
          ? this.$refs.topDownload.offsetHeight
          : 0
      this.$emit('layout-change', {
        visible: this.isVisible,
        height
      })
    },
    async installApp() {
      if (!this.deferredPrompt) {
        this.$toast({
          message:
            this.$lang.top_download_no_pwa ||
            'Add to Home Screen is not available here. Open in Chrome and ensure the app is served over HTTPS with a registered service worker.',
          duration: 3500
        })
        return
      }
      try {
        await this.deferredPrompt.prompt()
        await this.deferredPrompt.userChoice
      } catch (e) {
        this.$toast({
          message: this.$lang.top_download_install_fail || 'Install prompt failed',
          icon: 'cross'
        })
      } finally {
        this.deferredPrompt = null
      }
    }
  }
}
</script>
<style lang="less" scoped>
.top-download {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 10px 8px 8px;
  /* Figma: linear — 0% #0D4DA0 → 100% #013982 */
  background: linear-gradient(90deg, #0d4da0 0%, #013982 100%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.top-download__close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.top-download__lottie {
  flex-shrink: 0;
  width: 52px;
  height: 50px;
  overflow: hidden;
}

.top-download__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
}

.top-download__title {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #ffeb3b;
  line-height: 1.1;
  text-transform: uppercase;
}

.top-download__sub {
  font-size: 10px;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.95);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.top-download__cta {
  flex-shrink: 0;
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  background: #e53935;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.top-download__cta:active {
  opacity: 0.9;
}
</style>
